import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getClient } from '../../services/Service';

const hasAdminToken = (client) => {
  const token = client.admin_token;
  return typeof token === 'string' ? token.trim() !== '' : Boolean(token);
};

const getRegistrationDate = (client) => client.admin_created_at || client.created_at || null;

const parseDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    const mysqlMatch = trimmed.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );
    if (mysqlMatch) {
      const [, year, month, day, hour = '0', minute = '0', second = '0'] = mysqlMatch;
      return new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
      );
    }

    const formattedMatch = trimmed.match(/^(\d{2})\s([A-Za-z]{3})\s(\d{4})$/);
    if (formattedMatch) {
      const [, day, monthLabel, year] = formattedMatch;
      const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        .indexOf(monthLabel);

      if (monthIndex !== -1) {
        return new Date(Number(year), monthIndex, Number(day));
      }
    }
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isSameDay = (left, right) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const getStartOfWeek = (date) => {
  const start = new Date(date);
  const day = start.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  start.setDate(start.getDate() + diff);
  start.setHours(0, 0, 0, 0);
  return start;
};

const getRoleLabel = (role) => {
  if (role === 'T') return 'Ticketbot';
  if (role === 'L') return 'Leadbot';
  if (role === 'C') return 'Client Admin';
  return '-';
};

const formatDate = (value) => {
  const parsed = parseDate(value);
  if (!parsed) return '-';

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsed);
};

const StatCard = ({ title, value, accent, note }) => (
  <div
    className="rounded-4 p-4 h-100 text-white"
    style={{
      background: accent,
      boxShadow: '0 22px 44px rgba(15, 23, 42, 0.16)',
      minHeight: '170px',
    }}
  >
    <div className="d-flex flex-column justify-content-between h-100">
      <div>
        <div className="text-uppercase small fw-semibold opacity-75 mb-2">{title}</div>
        <div className="display-5 fw-bold">{value}</div>
      </div>
      <div className="small opacity-75">{note}</div>
    </div>
  </div>
);

const SuperAdminDashboard = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadClients = async () => {
      try {
        const response = await getClient();
        if (mounted && response?.status === 'success' && Array.isArray(response.data)) {
          setClients(response.data);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadClients();

    return () => {
      mounted = false;
    };
  }, []);

  const dashboardStats = useMemo(() => {
    const now = new Date();
    const weekStart = getStartOfWeek(now);
    const nextWeekStart = new Date(weekStart);
    nextWeekStart.setDate(nextWeekStart.getDate() + 7);

    const total = clients.length;
    const active = clients.filter((client) => hasAdminToken(client)).length;
    const inactive = clients.filter((client) => !hasAdminToken(client)).length;

    const registeredToday = clients.filter((client) => {
      const date = parseDate(getRegistrationDate(client));
      return date ? isSameDay(date, now) : false;
    }).length;

    const registeredThisWeek = clients.filter((client) => {
      const date = parseDate(getRegistrationDate(client));
      return date ? date >= weekStart && date < nextWeekStart : false;
    }).length;

    const registeredThisMonth = clients.filter((client) => {
      const date = parseDate(getRegistrationDate(client));
      return date
        ? date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth()
        : false;
    }).length;

    return {
      total,
      active,
      inactive,
      registeredToday,
      registeredThisWeek,
      registeredThisMonth,
    };
  }, [clients]);

  const latestClients = useMemo(
    () =>
      [...clients]
        .sort((left, right) => {
          const leftDate = parseDate(getRegistrationDate(left));
          const rightDate = parseDate(getRegistrationDate(right));
          return (rightDate?.getTime() || 0) - (leftDate?.getTime() || 0);
        })
        .slice(0, 5),
    [clients]
  );

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status" />
          <div className="fw-semibold text-muted">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
      <section
        className="rounded-4 p-4 p-lg-5 mb-4 text-white"
        style={{
          background:
            'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(30, 41, 59) 48%, rgb(37, 99, 235) 100%)',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.18)',
        }}
      >
        <div className="row align-items-center g-4">
          <div className="col-lg-8">
            <div className="text-uppercase small fw-semibold opacity-75 mb-2">Control Center</div>
            <h1 className="fw-bold mb-3" style={{ letterSpacing: '-0.03em' }}>
              Super Admin Dashboard
            </h1>
            <p className="mb-0 fs-5 opacity-75">
              Monitor onboarding volume, activation health, and client mix from one place.
            </p>
          </div>
          <div className="col-lg-4">
            <div className="d-flex flex-column gap-3">
              <Link to="/superadmin/components/add-client" className="btn btn-light btn-lg fw-semibold">
                Add New Client
              </Link>
              <Link to="/superadmin/components/all-client" className="btn btn-outline-light btn-lg fw-semibold">
                Manage All Clients
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="row g-4 mb-4">
        <div className="col-md-6 col-xl-4">
          <StatCard
            title="Total Clients"
            value={dashboardStats.total}
            accent="linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)"
            note="All registered clients on the platform"
          />
        </div>
        <div className="col-md-6 col-xl-4">
          <StatCard
            title="Active Clients"
            value={dashboardStats.active}
            accent="linear-gradient(135deg, #14532d 0%, #22c55e 100%)"
            note="Clients with a token set for their admin account"
          />
        </div>
        <div className="col-md-6 col-xl-4">
          <StatCard
            title="Inactive Clients"
            value={dashboardStats.inactive}
            accent="linear-gradient(135deg, #7f1d1d 0%, #ef4444 100%)"
            note="Clients with no token set on the admin account"
          />
        </div>
        <div className="col-md-6 col-xl-4">
          <StatCard
            title="Registered Today"
            value={dashboardStats.registeredToday}
            accent="linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)"
            note="Clients registered on today's date"
          />
        </div>
        <div className="col-md-6 col-xl-4">
          <StatCard
            title="This Week"
            value={dashboardStats.registeredThisWeek}
            accent="linear-gradient(135deg, #4c1d95 0%, #8b5cf6 100%)"
            note="Clients registered from Monday to today"
          />
        </div>
        <div className="col-md-6 col-xl-4">
          <StatCard
            title="This Month"
            value={dashboardStats.registeredThisMonth}
            accent="linear-gradient(135deg, #92400e 0%, #f59e0b 100%)"
            note="Clients registered in the current calendar month"
          />
        </div>
      </section>

      <section className="row g-4">
        <div className="col-xl-8">
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div>
                  <h4 className="mb-1 fw-bold">Latest Client Snapshot</h4>
                  <p className="text-muted mb-0">Recent clients with status, type, and registration date.</p>
                </div>
                <Link to="/superadmin/components/all-client" className="btn btn-outline-primary fw-semibold">
                  Open Client List
                </Link>
              </div>

              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Email</th>
                      <th>Slug</th>
                      <th>Type</th>
                      <th>Registered</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {latestClients.length > 0 ? (
                      latestClients.map((client) => {
                        const isActive = hasAdminToken(client);
                        const statusLabel = isActive ? 'Active' : 'Inactive';
                        const statusClass = isActive ? 'success' : 'danger';

                        return (
                          <tr key={client.id}>
                            <td className="fw-semibold">{client.Name || '-'}</td>
                            <td>{client.admin_email || client.Email || '-'}</td>
                            <td>{client.client_slug || '-'}</td>
                            <td>{getRoleLabel(client.admin_role)}</td>
                            <td>{formatDate(getRegistrationDate(client))}</td>
                            <td>
                              <span className={`badge text-bg-${statusClass}`}>{statusLabel}</span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center text-muted py-4">
                          No client data available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-4">
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-body p-4">
              <h4 className="fw-bold mb-3">Admin Notes</h4>
              <div className="d-flex flex-column gap-3">
                <div className="rounded-4 p-3" style={{ background: '#eff6ff' }}>
                  <div className="fw-semibold text-primary mb-1">Activation logic</div>
                  <div className="text-muted small">
                    Active and inactive counts use `admin_token` from the API.
                  </div>
                </div>
                <div className="rounded-4 p-3" style={{ background: '#fef3c7' }}>
                  <div className="fw-semibold text-warning-emphasis mb-1">Registration tracking</div>
                  <div className="text-muted small">
                    Date-based counts use `admin_created_at` first, then fall back to `created_at`.
                  </div>
                </div>
                <div className="rounded-4 p-3" style={{ background: '#ecfdf5' }}>
                  <div className="fw-semibold text-success mb-1">Next step</div>
                  <div className="text-muted small">
                    Use the All Client screen to filter by date, switch type, and review client status.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SuperAdminDashboard;
